/**
 * Secure storage utility for sensitive data
 * Uses sessionStorage for tokens (cleared on tab close) for better security
 * Falls back to localStorage for backward compatibility
 */

const TOKEN_KEY = 'auth_token'
const USER_DATA_KEY = 'user_data'

/**
 * Get auth token from secure storage (sessionStorage)
 */
export const getAuthToken = (): string | null => {
  try {
    // Try sessionStorage first (more secure)
    const token = sessionStorage.getItem(TOKEN_KEY)
    if (token) return token
    
    // Fallback to localStorage for backward compatibility
    const legacyToken = localStorage.getItem(TOKEN_KEY)
    if (legacyToken) {
      // Migrate to sessionStorage
      sessionStorage.setItem(TOKEN_KEY, legacyToken)
      return legacyToken
    }
    
    return null
  } catch (e) {
    // If sessionStorage is not available, fallback to localStorage
    return localStorage.getItem(TOKEN_KEY)
  }
}

/**
 * Set auth token in secure storage (sessionStorage)
 */
export const setAuthToken = (token: string): void => {
  try {
    sessionStorage.setItem(TOKEN_KEY, token)
    // Also keep in localStorage for backward compatibility during migration
    localStorage.setItem(TOKEN_KEY, token)
  } catch (e) {
    // Fallback to localStorage if sessionStorage fails
    localStorage.setItem(TOKEN_KEY, token)
  }
}

/**
 * Remove auth token from secure storage
 */
export const removeAuthToken = (): void => {
  try {
    sessionStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(TOKEN_KEY)
  } catch (e) {
    localStorage.removeItem(TOKEN_KEY)
  }
}

/**
 * Get user data from storage
 * User data can stay in localStorage as it's less sensitive
 */
export const getUserData = (): any => {
  try {
    const userDataStr = localStorage.getItem(USER_DATA_KEY)
    if (userDataStr) {
      return JSON.parse(userDataStr)
    }
    return null
  } catch (e) {
    return null
  }
}

/**
 * Set user data in storage
 */
export const setUserData = (userData: Record<string, any>): void => {
  try {
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData))
  } catch (e) {
    // Silently fail if storage is full
  }
}

/**
 * Remove user data from storage
 */
export const removeUserData = (): void => {
  try {
    localStorage.removeItem(USER_DATA_KEY)
  } catch (e) {
    // Silently fail
  }
}

/**
 * Check if user is authenticated
 */
export const checkIsAuthenticated = (): boolean => {
  return getAuthToken() !== null
}

